import { useRef, useEffect, useLayoutEffect, useCallback } from "react";

/**
 * <textarea>의 높이를 내용에 맞춰 자동 조정해줍니다.
 *
 * @param {React.RefObject<HTMLTextAreaElement>} textareaRef
 * @param {string} value
 * @param {{ minHeight?: number, maxHeight?: number }} [options]
 */
export const useAutosizeTextArea = (textareaRef, value, { minHeight = 60, maxHeight = 150 } = {}) => {
  const resize = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = "auto"; // 일단 auto 로 늘려서 scrollHeight 갱신
    const h = Math.min(maxHeight, Math.max(minHeight, ta.scrollHeight));
    ta.style.height = `${h}px`;
  }, [minHeight, maxHeight, textareaRef]);

  // 마운트 시 & input 이벤트 & window resize
  useLayoutEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;

    // 초기 한 번
    resize();

    // 이벤트 바인딩
    ta.addEventListener("input", resize);
    window.addEventListener("resize", resize);

    return () => {
      ta.removeEventListener("input", resize);
      window.removeEventListener("resize", resize);
    };
  }, [resize]);

  // value 변경에도 재조정
  useLayoutEffect(() => {
    resize();
  }, [resize, value]);
};

/**
 * ref 엘리먼트를 드래그해서 리사이즈할 수 있게 해주는 훅
 *
 * @param {React.RefObject<HTMLElement>} ref    - 사이즈를 조절할 DOM 엘리먼트 ref
 * @param {(size: number) => void} setSize      - 새로운 크기를 state 등에 반영하는 콜백
 * @param {'x'|'y'} axis                        - 조절 축 ('x'면 가로, 'y'면 세로)
 * @param {number} [min=200]                    - 최소 크기
 * @param {number} [max=600]                    - 최대 크기
 * @returns {(e: React.PointerEvent) => void}   - 엘리먼트에 붙일 onPointerDown 핸들러
 */
export const useResizable = (ref, setSize, axis = "x", min = 200, max = 600) => {
  // 드래그 시작 위치 & 시작 시 크기 저장용
  const startPosRef = useRef(0);
  const startSizeRef = useRef(0);

  // Pointer이 움직일 때마다 호출
  const onPointerMove = useCallback(
    (e) => {
      const curPos = axis === "x" ? e.clientX : e.clientY;
      const delta = startPosRef.current - curPos;
      // startSize + delta 로 하면 오른쪽(아래)이 늘고, 왼쪽(위)이 줄어듭니다
      const next = Math.min(max, Math.max(min, startSizeRef.current + delta));
      setSize(next);
    },
    [axis, min, max, setSize]
  );

  // Pointer을 놓으면 이벤트 제거
  const onPointerUp = useCallback(() => {
    document.removeEventListener("pointermove", onPointerMove);
    document.removeEventListener("pointerup", onPointerUp);
  }, [onPointerMove]);

  // 드래그 시작 시 초기값 저장 & 이벤트 바인딩
  const onPointerDown = useCallback(
    (e) => {
      e.preventDefault();
      // 시작 위치 기록
      startPosRef.current = axis === "x" ? e.clientX : e.clientY;
      // 시작 크기 기록
      const rect = ref.current?.getBoundingClientRect();
      startSizeRef.current = rect ? rect[axis === "x" ? "width" : "height"] : 0;

      // 전역 이벤트 등록
      document.addEventListener("pointermove", onPointerMove);
      document.addEventListener("pointerup", onPointerUp);
    },
    [axis, onPointerMove, onPointerUp, ref]
  );

  // 언마운트 시 cleanup
  useEffect(() => {
    return () => {
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
    };
  }, [onPointerMove, onPointerUp]);

  return onPointerDown;
};
